import React, { useState, useEffect, useRef } from 'react';
import { comprovanteService } from '../../services/comprovanteService';
import Toast from '../ui/Toast';
import ConfirmModal from '../ui/ConfirmModal';

const ComprovanteUpload = ({ pedidoId, pedidoStatus, pedidoPago, onUploadComplete }) => {
  const [comprovantes, setComprovantes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const fileInputRef = useRef(null);

  // Estados para controle de visualização
  const [previewImage, setPreviewImage] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [previewIsPdf, setPreviewIsPdf] = useState(false);
  // Zoom da imagem: false = cabe na tela | true = largura total com rolagem
  const [previewZoom, setPreviewZoom] = useState(false);

  // Estados para toast e modal de confirmação
  const [toast, setToast] = useState({ show: false, message: '', type: 'info' });
  const [confirmModal, setConfirmModal] = useState({ 
    show: false, 
    comprovanteId: null, 
    nomeOriginal: '' 
  });

  // Controle de permissões - Só permite upload em pedidos Pendente e pagos
  const canUpload = pedidoStatus === 'Pendente' && pedidoPago;
  const maxFiles = 4;

  useEffect(() => {
    loadComprovantes();
  }, [pedidoId]);

  // Cleanup das URLs de blob quando o componente for desmontado
  useEffect(() => {
    return () => {
      comprovantes.forEach(comprovante => {
        if (comprovante.blobUrl) {
          URL.revokeObjectURL(comprovante.blobUrl);
        }
      });
    };
  }, [comprovantes]);

  // Funções helper para toast
  const showToast = (message, type = 'info') => {
    setToast({ show: true, message, type });
  };

  const hideToast = () => {
    setToast({ show: false, message: '', type: 'info' });
  };

  const loadComprovantes = async () => {
    setLoading(true);
    try {
      const result = await comprovanteService.getComprovantes(pedidoId);
      if (result.success) {
        // Usar URLs diretas sem blob (CORS já foi resolvido)
        setComprovantes(result.data);
      }
    } catch (error) {
      console.error('Erro ao carregar comprovantes:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (event) => {
    const files = Array.from(event.target.files);
    validateAndSetFiles(files);
  };

  const handleDrop = (event) => {
    event.preventDefault();
    setDragOver(false);
    
    const files = Array.from(event.dataTransfer.files);
    validateAndSetFiles(files);
  };

  const validateAndSetFiles = (files) => {
    // Verificar limite total de arquivos
    const currentCount = comprovantes.length;
    const newCount = files.length;
    
    if (currentCount + newCount > maxFiles) {
      showToast(`Máximo de ${maxFiles} comprovantes por pedido. Você já possui ${currentCount} arquivo(s).`, 'warning');
      return;
    }

    // Validar arquivos
    const validation = comprovanteService.validateFiles(files);
    if (!validation.isValid) {
      showToast(validation.errors.join(' | '), 'error');
      return;
    }

    setSelectedFiles(files);
  };

  const handleUpload = async () => {
    if (selectedFiles.length === 0) return;

    setUploading(true);
    try {
      const result = await comprovanteService.uploadComprovantes(pedidoId, selectedFiles);
      
      if (result.success) {
        showToast(result.message, 'success');
        setSelectedFiles([]);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        await loadComprovantes();
        
        if (onUploadComplete) {
          onUploadComplete(result.data);
        }
      } else {
        showToast(result.message, 'error');
      }
    } catch (error) {
      showToast('Erro ao fazer upload dos comprovantes', 'error');
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveComprovante = (comprovanteId, nomeOriginal) => {
    setConfirmModal({
      show: true,
      comprovanteId,
      nomeOriginal
    });
  };

  const confirmRemoveComprovante = async () => {
    try {
      const result = await comprovanteService.removeComprovante(confirmModal.comprovanteId);
      
      if (result.success) {
        showToast(result.message, 'success');
        await loadComprovantes();
      } else {
        showToast(result.message, 'error');
      }
    } catch (error) {
      showToast('Erro ao remover comprovante', 'error');
    } finally {
      setConfirmModal({ show: false, comprovanteId: null, nomeOriginal: '' });
    }
  };

  const cancelRemoveComprovante = () => {
    setConfirmModal({ show: false, comprovanteId: null, nomeOriginal: '' });
  };

  const handlePreviewImage = async (comprovante, event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    const isPdf = comprovanteService.isPdf(comprovante);
    setPreviewIsPdf(isPdf);
    setPreviewZoom(false);

    try {
      // Fazer fetch como blob para evitar problemas de download e preservar
      // o Content-Type real (importante para que PDF abra no iframe).
      const response = await fetch(comprovanteService.getComprovanteUrl(comprovante.nomeArquivo));
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);

      setPreviewImage({
        ...comprovante,
        url: blobUrl,
        isBlob: true
      });
      setShowPreview(true);
    } catch (error) {
      console.error('Erro ao carregar arquivo para preview:', error);
      // Fallback para URL absoluta resolvida pelo helper
      setPreviewImage({
        ...comprovante,
        url: comprovanteService.getComprovanteUrl(comprovante.nomeArquivo)
      });
      setShowPreview(true);
    }
  };

  const handleClosePreview = () => {
    if (previewImage?.isBlob && previewImage.url) {
      URL.revokeObjectURL(previewImage.url);
    }
    setPreviewImage(null);
    setShowPreview(false);
    setPreviewIsPdf(false);
    setPreviewZoom(false);
  };

  const clearSelectedFiles = () => {
    setSelectedFiles([]);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg border p-6">
        <div className="flex items-center justify-center">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
          <span className="ml-2 text-gray-600">Carregando comprovantes...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg border">
      {/* Header */}
      <div className="p-4 border-b bg-gray-50">
        <h3 className="text-lg font-medium text-gray-900">
          Comprovantes de Pagamento
        </h3>
        <p className="text-sm text-gray-600 mt-1">
          {comprovantes.length}/{maxFiles} comprovantes enviados
        </p>
      </div>

      <div className="p-6">
        {/* Área de Upload */}
        {canUpload && comprovantes.length < maxFiles && (
          <div className="mb-6">
            <div
              className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
                dragOver
                  ? 'border-blue-400 bg-blue-50'
                  : 'border-gray-300 hover:border-gray-400'
              }`}
              onDrop={handleDrop}
              onDragOver={(e) => {
                e.preventDefault();
                setDragOver(true);
              }}
              onDragLeave={() => setDragOver(false)}
            >
              <svg
                className="mx-auto h-12 w-12 text-gray-400"
                stroke="currentColor"
                fill="none"
                viewBox="0 0 48 48"
              >
                <path
                  d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                  strokeWidth={2}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>

              <div className="mt-4">
                <p className="text-sm text-gray-600">
                  <button
                    type="button"
                    className="font-medium text-blue-600 hover:text-blue-500"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    Clique para selecionar
                  </button>
                  {' '}ou arraste e solte os arquivos aqui
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  PNG, JPG, GIF, WEBP ou PDF até 2MB cada • Máximo {maxFiles} arquivos
                </p>
              </div>

              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept="image/*,application/pdf"
                onChange={handleFileSelect}
                className="hidden"
                disabled={uploading}
              />
            </div>

            {/* Arquivos Selecionados */}
            {selectedFiles.length > 0 && (
              <div className="mt-4">
                <h4 className="text-sm font-medium text-gray-700 mb-2">
                  Arquivos selecionados:
                </h4>
                <div className="space-y-2">
                  {selectedFiles.map((file, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between bg-gray-50 p-2 rounded"
                    >
                      <div className="flex items-center">
                        <svg className="h-4 w-4 text-gray-400 mr-2" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clipRule="evenodd" />
                        </svg>
                        <span className="text-sm text-gray-600">{file.name}</span>
                        <span className="text-xs text-gray-400 ml-2">
                          ({comprovanteService.formatFileSize(file.size)})
                        </span>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="flex gap-2 mt-3">
                  <button
                    type="button"
                    onClick={handleUpload}
                    disabled={uploading}
                    className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {uploading ? 'Enviando...' : `Enviar ${selectedFiles.length} arquivo(s)`}
                  </button>
                  <button
                    type="button"
                    onClick={clearSelectedFiles}
                    disabled={uploading}
                    className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 disabled:opacity-50"
                  >
                    Cancelar
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Status de Upload Bloqueado */}
        {!canUpload && (
          <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
            <div className="flex">
              <svg className="h-5 w-5 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              <div className="ml-3">
                <p className="text-sm text-yellow-800">
                  {!pedidoPago 
                    ? 'Este pedido precisa estar marcado como pago para receber comprovantes.'
                    : pedidoStatus !== 'Pendente'
                    ? `Apenas pedidos com status "Pendente" podem receber comprovantes. Status atual: "${pedidoStatus}".`
                    : 'Pedido deve estar pago e pendente para receber comprovantes.'
                  }
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Lista de Comprovantes */}
        {comprovantes.length > 0 ? (
          <div className="space-y-3">
            <h4 className="text-sm font-medium text-gray-700">
              Comprovantes enviados:
            </h4>
            {comprovantes.map((comprovante) => (
              <div
                key={comprovante.id}
                className="flex flex-col sm:flex-row sm:items-center sm:justify-between p-3 bg-gray-50 rounded-lg space-y-2 sm:space-y-0"
              >
                <div className="flex items-center flex-1 min-w-0">
                  {comprovanteService.isPdf(comprovante) ? (
                    <button
                      type="button"
                      onClick={(e) => handlePreviewImage(comprovante, e)}
                      title="Visualizar PDF"
                      className="h-12 w-12 flex items-center justify-center rounded bg-red-50 border border-red-200 text-red-600 hover:bg-red-100 flex-shrink-0"
                    >
                      <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                        <path fillRule="evenodd" d="M4 4a2 2 0 012-2h6l4 4v10a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm3 9a1 1 0 011-1h4a1 1 0 110 2H8a1 1 0 01-1-1zm0-3a1 1 0 011-1h4a1 1 0 110 2H8a1 1 0 01-1-1z" clipRule="evenodd" />
                      </svg>
                    </button>
                  ) : (
                    <img
                      src={comprovanteService.getComprovanteUrl(comprovante.nomeArquivo)}
                      alt={comprovante.nomeOriginal}
                      className="h-12 w-12 object-cover rounded cursor-pointer hover:opacity-80 flex-shrink-0"
                      onClick={(e) => handlePreviewImage(comprovante, e)}
                    />
                  )}
                  <div className="ml-3 min-w-0 flex-1">
                    <button
                      type="button"
                      onClick={(e) => handlePreviewImage(comprovante, e)}
                      className="text-sm font-medium text-gray-900 hover:text-blue-600 cursor-pointer transition-colors text-left block truncate w-full"
                    >
                      {comprovante.nomeOriginal}
                    </button>
                    <p className="text-xs text-gray-500 truncate">
                      {comprovanteService.formatFileSize(comprovante.tamanho)} • 
                      Enviado em {new Date(comprovante.uploadedAt).toLocaleDateString('pt-BR')} • 
                      por {comprovante.uploadedBy}
                    </p>
                  </div>
                </div>

                <div className="flex flex-row items-center gap-1 sm:gap-2 sm:ml-4 flex-shrink-0">
                  <button
                    type="button"
                    onClick={(e) => handlePreviewImage(comprovante, e)}
                    className="inline-flex items-center justify-center px-2 sm:px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-blue-700 bg-blue-100 hover:bg-blue-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                    <span className="hidden sm:inline ml-1">Visualizar</span>
                  </button>
                  {canUpload && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleRemoveComprovante(comprovante.id, comprovante.nomeOriginal);
                      }}
                      className="inline-flex items-center justify-center px-2 sm:px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-red-700 bg-red-100 hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-colors"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                      <span className="hidden sm:inline ml-1">Remover</span>
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8">
            <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <p className="mt-2 text-sm text-gray-500">
              Nenhum comprovante enviado ainda
            </p>
          </div>
        )}
      </div>

      {/* Modal de Preview */}
      {showPreview && previewImage && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-4xl max-h-full overflow-hidden flex flex-col">
            <div className="flex items-center justify-between p-4 border-b flex-shrink-0 gap-4">
              <h3 className="text-lg font-medium truncate">{previewImage.nomeOriginal}</h3>
              {!previewIsPdf && (
                <span className="text-sm text-gray-500 whitespace-nowrap ml-auto mr-2">
                  {previewZoom ? 'Clique na imagem para reduzir' : 'Clique na imagem para ampliar'}
                </span>
              )}
              <button
                type="button"
                onClick={handleClosePreview}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="p-4 overflow-auto flex-1 min-h-0">
              {previewIsPdf ? (
                <iframe
                  src={previewImage.url}
                  title={previewImage.nomeOriginal}
                  className="w-[80vw] h-[80vh] max-w-full border-0"
                />
              ) : (
                <img
                  src={previewImage.url}
                  alt={previewImage.nomeOriginal}
                  onClick={() => setPreviewZoom((zoom) => !zoom)}
                  title={previewZoom ? 'Clique para reduzir' : 'Clique para ampliar'}
                  className={
                    previewZoom
                      // Ampliado: ocupa a largura do modal e rola na vertical.
                      // Útil para prints estreitos, em que caber na tela deixa
                      // a imagem fina demais para ler.
                      // w-[80vw] (medida definida, igual ao iframe do PDF) em vez
                      // de w-full: o painel é shrink-to-fit, então uma largura
                      // percentual resolveria para o tamanho natural da imagem.
                      ? 'w-[80vw] max-w-full mx-auto cursor-zoom-out'
                      : 'max-w-full max-h-[80vh] w-auto mx-auto object-contain cursor-zoom-in'
                  }
                />
              )}
            </div>
          </div>
        </div>
      )}

      {/* Toast de notificações */}
      <Toast
        message={toast.message}
        type={toast.type}
        isVisible={toast.show}
        onClose={hideToast}
      />

      {/* Modal de confirmação de exclusão */}
      <ConfirmModal
        isOpen={confirmModal.show}
        onClose={cancelRemoveComprovante}
        onConfirm={confirmRemoveComprovante}
        title="Remover Comprovante"
        message={`Tem certeza que deseja remover o comprovante "${confirmModal.nomeOriginal}"? Esta ação não pode ser desfeita.`}
        confirmText="Remover"
        cancelText="Cancelar"
        type="danger"
      />
    </div>
  );
};

export default ComprovanteUpload;
